import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Play, Film, Tv, LogOut, Star, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PWAInstall from "@/components/PWAInstall";
import { BRAND } from "@/lib/brand";

export default function Navbar() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab") || "movies";
  const urlQuery = params.get("q") || "";
  const [query, setQuery] = useState(urlQuery);

  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (query.trim() && query !== urlQuery) {
        navigate(`/?tab=search&q=${encodeURIComponent(query.trim())}`, { replace: true });
      }
    }, 350);
    return () => clearTimeout(id);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const host = (() => {
    try {
      return new URL(session?.creds?.server?.startsWith("http") ? session.creds.server : `http://${session?.creds?.server}`).host;
    } catch {
      return session?.creds?.server;
    }
  })();

  const go = (t) => navigate(`/?tab=${t}`);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#05070A]/85 border-b border-white/10">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 h-16 flex items-center gap-4">
        <button
          onClick={() => go("movies")}
          className="flex items-center gap-2 shrink-0"
          data-testid="brand-home-button"
        >
          {BRAND.logoUrl ? (
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-7 sm:h-8 w-auto" />
          ) : (
            <>
              <div className="h-8 w-8 rounded-lg bg-[#14A3FF] flex items-center justify-center shadow-[0_0_18px_rgba(20,163,255,0.5)]">
                <Play className="h-4 w-4 text-white fill-white" />
              </div>
              <span className="text-lg font-extrabold tracking-tight text-white font-heading hidden sm:block">
                {BRAND.name}
              </span>
            </>
          )}
        </button>

        <nav className="flex items-center gap-1 ml-2">
          <button
            data-testid="nav-movies-tab"
            onClick={() => go("movies")}
            className={`flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === "movies" ? "bg-[#14A3FF] text-white" : "text-slate-300 hover:bg-white/5"
            }`}
          >
            <Film className="h-4 w-4" /> Movies
          </button>
          <button
            data-testid="nav-series-tab"
            onClick={() => go("series")}
            className={`flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === "series" ? "bg-[#14A3FF] text-white" : "text-slate-300 hover:bg-white/5"
            }`}
          >
            <Tv className="h-4 w-4" /> Series
          </button>
          <button
            data-testid="nav-mylist-tab"
            onClick={() => go("mylist")}
            className={`flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === "mylist" ? "bg-[#14A3FF] text-white" : "text-slate-300 hover:bg-white/5"
            }`}
          >
            <Star className="h-4 w-4" /> My List
          </button>
        </nav>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (query.trim()) navigate(`/?tab=search&q=${encodeURIComponent(query.trim())}`);
          }}
          className="flex-1 max-w-md ml-2 hidden sm:block"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="global-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search movies & series…"
              className="pl-9 h-9 bg-[#131924] border-white/10 text-white placeholder:text-slate-600"
            />
          </div>
        </form>

        <div className="ml-auto sm:ml-3 flex items-center gap-2 sm:gap-3">
          <button
            data-testid="mobile-search-button"
            onClick={() => navigate("/?tab=search")}
            className="sm:hidden h-9 w-9 rounded-full flex items-center justify-center text-slate-300 hover:bg-white/5"
          >
            <Search className="h-5 w-5" />
          </button>
          <div className="hidden lg:flex items-center gap-1.5 text-xs text-slate-400">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="max-w-[140px] truncate">{host}</span>
          </div>
          <PWAInstall />
          <Button
            data-testid="disconnect-button"
            onClick={logout}
            variant="ghost"
            size="sm"
            className="text-slate-300 hover:text-white hover:bg-white/5"
          >
            <LogOut className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Disconnect</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
